import React from 'react';
import Router, { withRouter } from 'next/router';
import classNames from 'classnames';
import Helmet from 'next/head';
import PropTypes from 'prop-types';
import { IntlProvider } from 'react-intl';
import { Provider } from 'mobx-react';
import IdleTimer from 'react-idle-timer';
import config from '../../../config';
import store from '../../../store/ProductStore';
import PLPStore from '../../../store/PLPStore';
import Header from '../Header';
import Footer from '../Footer';
import TextField from '../../Generic/TextField/TextField';
import CookieConcentBtn from '../CookieConcentBtn/CookieConsentBtn';
import Typography from '../../Generic/Typography';
import path from '../../../assets/images/svgImages.svg';
import styles from '../Header/Header-tw-styles';
import userStore from '../../../store/UserStore';
import i18nMessages from '../../../constants/messages/pt-BR';
import { hideElementsBySelectors, restoreContentForScreenReaders } from '../../../utils/ariaHiddenHelper';

const spritePath = typeof path === 'string' ? path : path?.src || '';

export class Layout extends React.Component {
  state = {
    search_text: '',
    showHide: false,
    widthVar: null,
  };
  
  searchModalRef = React.createRef();
  hiddenElements = [];
  searchTriggerElement = null;

  componentDidMount() {
    this.setState({ widthVar: window.innerWidth });
    window.addEventListener(i18nMessages.resize, this.resetOnResize);
  }

  componentWillUnmount() {
    window.removeEventListener(i18nMessages.resize, this.resetOnResize);
  }

  resetOnResize = () => {
    const { widthVar } = this.state;
    if (widthVar !== window.innerWidth) {
      this.setState({
        search_text: '',
        showHide: false,
        widthVar: window.innerWidth,
      });
    }
  };
  stripTrailingSlash = url => {
    return url.endsWith('/') ? url.slice(0, -1) : url;
  };
  submitHandler = event => {
    event.preventDefault();
    const { layoutData } = this.props;
    const { search_text } = this.state;
    event.preventDefault();
    const normalizedQuery = search_text.trim();
    if (normalizedQuery !== '') {
      Router.push(
        `/${layoutData.locale.toLowerCase()}/search-results?q=${encodeURIComponent(normalizedQuery)}`
      );
    }
  };
  searchInputValue = event => {
    this.setState({ search_text: event.target.value });
  };
  toggleShowHide = () => {
    const { showHide } = this.state;
    this.setState(
      {
        showHide: !showHide,
      },
      () => {
        const body = document.getElementsByTagName(i18nMessages.body);
        const bodyContent = document.getElementById(i18nMessages.content);
        const header = document.getElementById(i18nMessages.pageheader);
        const footer = document.getElementById(i18nMessages.footer);
        if (!showHide) {
          // Save the element that triggered the modal (search button)
          this.searchTriggerElement = document.activeElement;

          body[0].classList.add(i18nMessages.overflowhidden);
          bodyContent.classList.add(i18nMessages.blur8);
          header.classList.add(i18nMessages.blur8);
          footer.classList.add(i18nMessages.blur8);
          
          // Hide content from screen readers when search modal opens
          this.hiddenElements = hideElementsBySelectors([
            '#page-header',
            '#content',
            '#page-footer'
          ]);
          
          // Focus on search input and setup focus trap
          if (this.searchModalRef.current) {
            const searchInput = this.searchModalRef.current.querySelector('input');
            if (searchInput) {
              searchInput.focus();
            }
            
            // Setup focus trap and keyboard handlers
            this.setupSearchModalFocusTrap();
          }
        } else {
          body[0].classList.remove(i18nMessages.overflowhidden);
          bodyContent.classList.remove(i18nMessages.blur8);
          header.classList.remove(i18nMessages.blur8);
          footer.classList.remove(i18nMessages.blur8);
          
          // Restore content for screen readers when search modal closes
          restoreContentForScreenReaders(this.hiddenElements);
          this.hiddenElements = [];
          
          // Remove event listeners
          document.removeEventListener('keydown', this.handleSearchModalTab);
          document.removeEventListener('keydown', this.handleSearchModalEscape);

          // Return focus to the element that triggered the modal
          if (this.searchTriggerElement && this.searchTriggerElement.focus) {
            this.searchTriggerElement.focus();
            this.searchTriggerElement = null;
          }
        }
      }
    );
  };

  setupSearchModalFocusTrap = () => {
    // Handle Tab key to trap focus within search modal
    this.handleSearchModalTab = (e) => {
      if (e.key === 'Tab' && this.searchModalRef.current) {
        const focusableElements = Array.from(
          this.searchModalRef.current.querySelectorAll(
            'button, [href], input, [tabindex]:not([tabindex="-1"])'
          )
        );
        
        if (focusableElements.length === 0) return;

        const activeElement = document.activeElement;
        const firstElement = focusableElements[0];
        const lastElement = focusableElements[focusableElements.length - 1];

        if (e.shiftKey) {
          // Shift+Tab on first element should go to last element
          if (activeElement === firstElement) {
            e.preventDefault();
            lastElement.focus();
          }
        } else {
          // Tab on last element should go to first element
          if (activeElement === lastElement) {
            e.preventDefault();
            firstElement.focus();
          }
        }
      }
    };

    // Handle Escape key to close search modal
    this.handleSearchModalEscape = (e) => {
      if (e.key === 'Escape') {
        this.onSearchClose();
      }
    };

    document.addEventListener('keydown', this.handleSearchModalTab);
    document.addEventListener('keydown', this.handleSearchModalEscape);
  };
  onSearchClose = () => {
    this.toggleShowHide();
  };

  render() {
    const { children, layoutData, router } = this.props;
    const { showHide, search_text} = this.state;
    var document;
    const onIdle = () => {
      const timer = setInterval(() => {
        clearInterval(timer);
        if (!localStorage.getItem('janrainCaptureToken')) return;
        document = window.document;
        localStorage.removeItem('janrainCaptureProfileData');
        localStorage.removeItem('janrainCaptureToken');
        localStorage.removeItem('janrainCaptureReturnExperienceData');
        localStorage.removeItem('janrainCaptureToken_Expires');
        localStorage.removeItem('janrainCaptureReturnExperienceData_Expires');
        window.location = '/pt-br';
      }, 200);
    };

    return (
      <Provider productStore={store} PLPStore={PLPStore} userStore={userStore}>
        <IntlProvider
          locale={layoutData && layoutData.locale}
          messages={i18nMessages}
        >
          <>
            <IdleTimer
              ref={ref => ref}
              element={document}
              onIdle={onIdle}
              debounce={250}
              timeout={1000 * 60 * 30}
            />

            <div role="main">
              {router && (
                <Helmet>
                  <link
                    href={this.stripTrailingSlash(
                      `${config.siteUrl}${router.asPath.split('?')[0]}`
                    )}
                    rel="canonical"
                  />
                </Helmet>
              )}
              <Header
                headerData={layoutData && layoutData.headerCollection.items[0]}
                locale={layoutData.locale}
                toggleShowHide={this.toggleShowHide}
                isSearchExpanded={this.state.showHide}
                userStore={userStore}
              />
              <CookieConcentBtn
                locale={layoutData.locale}
                enabled
                onClick={['Optanon', 'ToggleInfoDisplay']}
                href="javascript:void(0)"
              />
              <div id="content">{children}</div>
              <Footer
                footerData={layoutData && layoutData.footerCollection.items[0]}
                locale={layoutData && layoutData.locale}
              />
            </div>
            <div
              ref={this.searchModalRef}
              id="search-modal"
              className={classNames(
                styles.container1,
                styles.searchContainer,
                showHide ? 'visible' : 'hidden'
              )}
              role="dialog"
              aria-modal="true"
              aria-label="Search modal"
            >
              <div
                role="presentation"
                id={i18nMessages.searchCloseDiv}
                className={styles.searchCloseDiv}
                onClick={this.onSearchClose}
              />
              <form
                onSubmit={this.submitHandler}
                className={classNames(styles.searchform)}
              >
                <Typography
                  component="div"
                  className={classNames(styles.inputTextField)}
                >
                  {i18nMessages.inputField}
                </Typography>
                <TextField
                  className={classNames(styles.searchField)}
                  value={search_text}
                  onChange={this.searchInputValue}
                  placeholder=" "
                  autoFocus
                  aria-label="buscar expandido, selecionado"
                />
                <button
                  type="submit"
                  className={classNames(styles.searchButton, 'event_search')}
                  data-action-detail="search"
                  aria-label="buscar"
                  onSubmit={event => this.submitHandler(event)}
                >
                  <svg className={classNames(styles.searchIcon)}>
                    <use xlinkHref={`${spritePath}#white-search`} />
                  </svg>
                </button>
              </form>
            </div>
          </>
        </IntlProvider>
      </Provider>
    );
  }
}

Layout.propTypes = {
  children: PropTypes.node.isRequired,
  layoutData: PropTypes.oneOfType([PropTypes.object]).isRequired,
  router: PropTypes.oneOfType([PropTypes.object]).isRequired,
};

export default withRouter(Layout);
